import React, { useEffect, useMemo, useState } from "react";
import { bookAppointment, getAvailability } from "../APIs/api-routes.util";
import { AvailabilityResponse, BookResponse } from "../APIs/api.types";
import GetPhoneBookings from "./GetPhoneBookings";
import {
  BookingType,
  availabilityMessage,
  buildBookingConfirmation,
} from "../common/booking.util";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal, ModalFooter } from "../components/ui/Modal";
import { LoadingOverlay } from "../components/ui/PagePrimitives";
import { useAppSettings } from "../context/AppSettingsContext";
import { apiSubscribe } from "../lib/api-subscribe";

function AdminBooking() {
  const { urgentEnabled } = useAppSettings();
  const [alert, setAlert] = useState<{ variant: "success" | "error" | "warning" | "info"; message: string } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limits, setLimits] = useState<{ urgent: number; normal: number } | null>(null);
  const today = new Date().toISOString().split("T")[0];
  const [bookingType, setBookingType] = useState<BookingType>("normal");
  const [confirmation, setConfirmation] = useState("");
  const [data, setData] = useState({
    phone: "",
    date: today,
    name: "",
    guardianName: "",
  });

  const effectiveBookingType: BookingType = urgentEnabled ? bookingType : "normal";

  const resetAvailability = () => {
    setIsEnabled(false);
    setConfirmation("");
    setAlert(null);
    setLimits(null);
  };

  useEffect(() => {
    if (!urgentEnabled && bookingType !== "normal") {
      setBookingType("normal");
      resetAvailability();
    }
  }, [urgentEnabled, bookingType]);

  const checkDate = () => {
    if (!data.date) {
      setAlert({ variant: "error", message: "Please select a date." });
      return;
    }
    setLoading(true);
    apiSubscribe<AvailabilityResponse>(
      getAvailability(data.date, effectiveBookingType),
      {
        next: (response) => {
          setLoading(false);
          setLimits({
            urgent: response.urgent_limit ?? 0,
            normal: response.normal_limit ?? 0,
          });
          const result = availabilityMessage(effectiveBookingType, response);
          setIsEnabled(result.available);
          setAlert({
            variant: result.available ? "success" : "error",
            message: result.message,
          });
        },
        error: () => {
          setLoading(false);
          setAlert({ variant: "error", message: "Could not check availability." });
        },
      }
    );
  };

  const formData = useMemo(() => {
    const fd = new FormData();
    fd.append("name", data.name);
    fd.append("date", data.date);
    fd.append("guardianName", data.guardianName);
    fd.append("phone", data.phone);
    fd.append("booking_type", effectiveBookingType);
    return fd;
  }, [data, effectiveBookingType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.date || !data.name || !data.guardianName || !data.phone) {
      setAlert({ variant: "error", message: "Please complete all required fields." });
      return;
    }
    setLoading(true);
    apiSubscribe<BookResponse>(
      bookAppointment(formData),
      {
        next: (response) => {
          setLoading(false);
          if (response?.token_number) {
            const text = buildBookingConfirmation(
              response.token_number,
              data.date,
              response.booking_type === "urgent" ? "urgent" : "normal"
            );
            setConfirmation(text);
            setAlert({ variant: "success", message: text });
          } else {
            setConfirmation("");
            setAlert({
              variant: "error",
              message: response?.error || "Booking failed. Please try again.",
            });
          }
        },
        error: () => {
          setLoading(false);
          setAlert({ variant: "error", message: "Could not reach server." });
        },
      }
    );
  };

  const urgentCap = limits?.urgent;

  return (
    <>
      <LoadingOverlay show={loading} />

      {alert && (
        <Alert
          variant={alert.variant === "error" ? "error" : alert.variant === "success" ? "success" : alert.variant === "warning" ? "warning" : "info"}
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {confirmation && (
        <div className="rounded-xl border border-green-200 bg-status-success-bg p-4 text-sm leading-relaxed text-status-success shadow-soft">
          {confirmation}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-xl border border-brand-100 border-t-4 border-t-brand-500 bg-surface p-4 shadow-soft"
      >
        {urgentEnabled ? (
          <div>
            <p className="mb-2 text-sm font-medium text-content-primary">Booking type</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setBookingType("normal");
                  resetAvailability();
                }}
                className={`rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                  bookingType === "normal"
                    ? "border-accent-emerald bg-accent-emerald-light text-accent-emerald-dark"
                    : "border-border bg-surface text-content-muted hover:border-brand-200"
                }`}
              >
                <span className="block text-sm font-semibold">Normal</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setBookingType("urgent");
                  resetAvailability();
                }}
                className={`rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                  bookingType === "urgent"
                    ? "border-accent-rose bg-accent-rose-light text-accent-rose-dark"
                    : "border-border bg-surface text-content-muted hover:border-brand-200"
                }`}
              >
                <span className="block text-sm font-semibold">Urgent</span>
              </button>
            </div>
            <p className="mt-1.5 text-2xs text-content-muted">Clinic closed Wednesday and Sunday.</p>
          </div>
        ) : (
          <p className="text-2xs text-content-muted">Clinic closed Wednesday and Sunday.</p>
        )}

        {urgentEnabled && bookingType === "urgent" && (
          <Alert variant="warning">
            {urgentCap != null
              ? `Up to ${urgentCap} urgent bookings per day on this date. Expected arrival at 2:00 PM.`
              : "Check availability to see the urgent limit for this date. Expected arrival at 2:00 PM."}
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          <Input
            label="Appointment date"
            type="date"
            value={data.date}
            onChange={(e) => {
              setData({ ...data, date: e.target.value });
              resetAvailability();
            }}
          />
          <Button type="button" variant="secondary" fullWidth onClick={checkDate}>
            Check availability
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          <Input
            label="Phone number"
            type="tel"
            placeholder="923001234567"
            value={data.phone}
            onChange={(e) => setData({ ...data, phone: e.target.value })}
          />
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => setShowHistory(true)}
            disabled={!data.phone}
          >
            View history
          </Button>
        </div>

        <Input
          label="Patient name"
          placeholder="Full name"
          value={data.name}
          disabled={!isEnabled}
          onChange={(e) => setData({ ...data, name: e.target.value })}
        />
        <Input
          label="Guardian name"
          placeholder="Father / husband name"
          value={data.guardianName}
          disabled={!isEnabled}
          onChange={(e) => setData({ ...data, guardianName: e.target.value })}
        />

        <Button type="submit" disabled={!isEnabled} fullWidth size="lg">
          Book {effectiveBookingType === "urgent" ? "urgent" : "normal"} appointment
        </Button>
      </form>

      <Modal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        title="Previous appointments"
        description="Select a record to pre-fill patient details."
        size="lg"
        footer={
          <ModalFooter>
            <Button variant="secondary" onClick={() => setShowHistory(false)}>
              Close
            </Button>
          </ModalFooter>
        }
      >
        <GetPhoneBookings
          phone={data.phone}
          onSelect={(items) => {
            setData({ ...data, name: items.name, guardianName: items.guardianName });
            setShowHistory(false);
          }}
          onLoading={setLoading}
        />
      </Modal>
    </>
  );
}

export default AdminBooking;
